import { useState, useEffect, useCallback } from "react";
import { Code2, Play, Save, RefreshCw, AlertTriangle, CheckCircle2, Lock, Zap, FileText } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

const TEMPLATES = [
  {
    id: "jingle_rotation",
    name: "Jingle Every 3 Songs",
    desc: "Insert a jingle/sweeper from a playlist after every 3 music tracks",
    color: "purple",
    crossfade_type: "smart",
    crossfade: 1.5,
    code: `# Jingle Rotation — plays a jingle after every 3 songs
# Create a playlist called "Jingles" in AzuraCast first,
# then reference it here. AzuraCast auto-generates the
# playlist variables (e.g. playlist_jingles).
#
# radio = rotate(weights=[1, 1, 1, 1],
#   [music, music, music, jingles]
# )`,
  },
  {
    id: "smooth_crossfade",
    name: "Smooth Jazz Crossfade",
    desc: "Long, gentle 5-second crossfade with volume ducking — perfect for chill/jazz formats",
    color: "blue",
    crossfade_type: "smart",
    crossfade: 5,
    code: `# Smooth crossfade with long overlap
# Great for jazz, lo-fi, ambient, and chill stations
#
# def smooth_fade(a, b)
#   add(normalize=false, [
#     fade.in(initial_metadata=false, duration=5., b),
#     fade.out(duration=5., a)
#   ])
# end
# radio = cross(duration=5., smooth_fade, radio)`,
  },
  {
    id: "talk_show",
    name: "Talk Show Format",
    desc: "Quick cuts with no crossfade — ideal for talk, podcast, and news formats",
    color: "green",
    crossfade_type: "none",
    crossfade: 0,
    code: `# Talk show format — hard cuts, no crossfade
# Keeps speech clear without music blending
#
# radio = switch(track_sensitive=true, [
#   ({ 0h-6h }, night_playlist),
#   ({ 6h-10h }, morning_show),
#   ({ 10h-15h }, midday_playlist),
#   ({ 15h-19h }, afternoon_drive),
#   ({ 19h-24h }, evening_playlist),
# ])`,
  },
  {
    id: "night_mode",
    name: "Night Mode (Quieter)",
    desc: "Automatically reduces volume during late night hours (12AM - 6AM)",
    color: "yellow",
    crossfade_type: "smart",
    crossfade: 3,
    code: `# Night mode — reduce volume during late hours
# Lowers output between midnight and 6 AM
#
# radio = switch(
#   [
#     ({ 0h-6h }, amplify(0.6, radio)),
#     ({ true }, radio),
#   ]
# )`,
  },
  {
    id: "dj_fallback",
    name: "DJ Fallback Chain",
    desc: "Smart fallback: Live DJ > Scheduled Playlist > AutoDJ > Emergency track",
    color: "red",
    crossfade_type: "normal",
    crossfade: 2,
    code: `# DJ priority fallback chain
# If live DJ disconnects, falls back gracefully
#
# radio = fallback(track_sensitive=false, [
#   live_dj,
#   scheduled_playlist,
#   autodj,
#   single("~/emergency.mp3"),
# ])`,
  },
  {
    id: "top_of_hour",
    name: "Top-of-Hour ID",
    desc: "Play your station ID at the top of every hour",
    color: "orange",
    crossfade_type: "normal",
    crossfade: 1,
    code: `# Top-of-hour station ID
# Plays a station identification file at :00 every hour
#
# station_id = single("~/station_id.mp3")
# radio = fallback(track_sensitive=false, [
#   switch([({ 0m-1m }, station_id)]),
#   radio,
# ])`,
  },
];

const TEMPLATE_COLORS = {
  purple: { bg: "bg-purple-500/10", border: "border-purple-500/20", text: "text-purple-400", hover: "hover:border-purple-500/40" },
  blue: { bg: "bg-blue-500/10", border: "border-blue-500/20", text: "text-blue-400", hover: "hover:border-blue-500/40" },
  green: { bg: "bg-green-500/10", border: "border-green-500/20", text: "text-green-400", hover: "hover:border-green-500/40" },
  yellow: { bg: "bg-yellow-500/10", border: "border-yellow-500/20", text: "text-yellow-400", hover: "hover:border-yellow-500/40" },
  red: { bg: "bg-red-500/10", border: "border-red-500/20", text: "text-red-400", hover: "hover:border-red-500/40" },
  orange: { bg: "bg-orange-500/10", border: "border-orange-500/20", text: "text-orange-400", hover: "hover:border-orange-500/40" },
};

export const LiquidsoapTab = ({ currentUser, authToken }) => {
  const [config, setConfig] = useState(null);
  const [customCode, setCustomCode] = useState("");
  const [crossfadeType, setCrossfadeType] = useState("normal");
  const [crossfade, setCrossfade] = useState(2);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const isPro = ["professional", "premium", "premium", "admin"].includes(currentUser?.plan);

  const fetchConfig = useCallback(async () => {
    if (!isPro) { setLoading(false); return; }
    try {
      const res = await fetch(`${BACKEND_URL}/api/station/liquidsoap-config`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        setCustomCode(data.custom_config || "");
        setCrossfadeType(data.crossfade_type || "normal");
        setCrossfade(data.crossfade ?? 2);
      }
    } catch (err) {
      console.error("Failed to fetch Liquidsoap config:", err);
    } finally {
      setLoading(false);
    }
  }, [authToken, isPro]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    setMessage({ text: "", type: "" });
    try {
      const res = await fetch(`${BACKEND_URL}/api/station/liquidsoap-config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({
          custom_config: customCode,
          crossfade_type: crossfadeType,
          crossfade: Number(crossfade),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Save failed");
      setMessage({
        text: data.message || "Configuration saved!",
        type: "success",
      });
      setTimeout(() => setMessage({ text: "", type: "" }), 5000);
    } catch (err) {
      setMessage({ text: err.message, type: "error" });
    } finally {
      setSaving(false);
    }
  };

  // Locked view for starter plan
  if (!isPro) {
    return (
      <div className="flex flex-col items-center justify-center py-20" data-testid="liquidsoap-locked">
        <div className="w-20 h-20 rounded-2xl bg-purple-500/10 flex items-center justify-center mb-6">
          <Lock className="w-10 h-10 text-purple-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Advanced Station Control</h2>
        <p className="text-white/50 text-center max-w-md mb-6">
          Custom Liquidsoap configuration lets you control crossfades, jingles, scheduling logic, and more.
          Available on Professional and Premium plans.
        </p>
        <a
          href="/#pricing"
          className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-xl transition font-medium"
          data-testid="liquidsoap-upgrade-btn"
        >
          Upgrade Your Plan
        </a>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 text-purple-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="liquidsoap-editor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
            <Code2 className="w-5 h-5 text-orange-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Liquidsoap Configuration</h2>
            <p className="text-white/50 text-sm">
              Inject custom Liquidsoap code into your station's AutoDJ engine
            </p>
          </div>
        </div>
        <button
          onClick={fetchConfig}
          className="text-white/50 hover:text-white transition p-2"
          title="Refresh from AzuraCast"
          data-testid="liquidsoap-refresh"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Crossfade Settings */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
        <h3 className="text-white font-semibold mb-4">Crossfade Settings</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-white/60 text-sm mb-1.5">Crossfade Type</label>
            <select
              value={crossfadeType}
              onChange={(e) => setCrossfadeType(e.target.value)}
              className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-purple-500/50 transition"
              data-testid="crossfade-type-select"
            >
              <option value="normal">Normal</option>
              <option value="smart">Smart (Volume-aware)</option>
              <option value="none">Disabled</option>
            </select>
          </div>
          <div>
            <label className="block text-white/60 text-sm mb-1.5">
              Crossfade Duration: <span className="text-purple-400">{crossfade}s</span>
            </label>
            <input
              type="range"
              min="0"
              max="30"
              step="0.5"
              value={crossfade}
              onChange={(e) => setCrossfade(parseFloat(e.target.value))}
              className="w-full h-2 rounded-full appearance-none cursor-pointer bg-white/10 accent-purple-500"
              data-testid="crossfade-duration-slider"
            />
            <div className="flex justify-between text-white/30 text-xs mt-1">
              <span>0s</span>
              <span>15s</span>
              <span>30s</span>
            </div>
          </div>
        </div>
      </div>

      {/* Preset Templates */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Zap className="w-5 h-5 text-purple-400" />
          <h3 className="text-white font-semibold">Quick Templates</h3>
          <span className="text-white/30 text-xs ml-auto">Click to load into editor</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {TEMPLATES.map((tpl) => {
            const c = TEMPLATE_COLORS[tpl.color] || TEMPLATE_COLORS.purple;
            return (
              <button
                key={tpl.id}
                onClick={() => {
                  const prefix = customCode.trim() ? customCode + "\n\n" : "";
                  setCustomCode(prefix + tpl.code);
                  setCrossfadeType(tpl.crossfade_type);
                  setCrossfade(tpl.crossfade);
                  setMessage({ text: `"${tpl.name}" template loaded. Review the code and hit Save & Apply.`, type: "success" });
                  setTimeout(() => setMessage({ text: "", type: "" }), 4000);
                }}
                className={`text-left ${c.bg} border ${c.border} ${c.hover} rounded-xl p-3.5 transition-all hover:scale-[1.01]`}
                data-testid={`template-${tpl.id}`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <FileText className={`w-4 h-4 ${c.text}`} />
                  <span className={`text-sm font-semibold ${c.text}`}>{tpl.name}</span>
                </div>
                <p className="text-white/40 text-xs leading-relaxed">{tpl.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Code Editor */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold">Custom Liquidsoap Code</h3>
          <span className="text-orange-400/60 text-xs bg-orange-500/10 px-2 py-1 rounded-lg">
            Advanced
          </span>
        </div>

        {/* Warning */}
        <div className="flex items-start gap-3 bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-3 mb-4">
          <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="text-yellow-300 font-medium">Use with caution</p>
            <p className="text-yellow-200/60">
              Invalid Liquidsoap code can stop your station's AutoDJ. Changes are applied immediately after save. 
              This code is injected into your station's generated liquidsoap.liq file.
            </p>
          </div>
        </div>

        <textarea
          value={customCode}
          onChange={(e) => setCustomCode(e.target.value)}
          placeholder={`# Custom Liquidsoap code examples:\n\n# Add a jingle every 3 songs:\n# radio = rotate(weights=[1,1,1,1], [radio, jingles])\n\n# Custom crossfade function:\n# def my_crossfade(a, b)\n#   add(normalize=false,\n#     [fade.in(duration=2., b), fade.out(duration=2., a)])\n# end`}
          className="w-full h-64 bg-black/60 border border-white/10 rounded-xl px-4 py-3 text-green-300 font-mono text-sm focus:outline-none focus:border-orange-500/50 transition resize-y"
          spellCheck="false"
          data-testid="liquidsoap-code-editor"
        />

        <p className="text-white/30 text-xs mt-2">
          Tip: This code appends to AzuraCast's generated Liquidsoap config. Use standard Liquidsoap syntax.
        </p>
      </div>

      {/* Status Message */}
      {message.text && (
        <div
          className={`flex items-center gap-2 p-3 rounded-xl ${
            message.type === "success"
              ? "bg-green-500/10 border border-green-500/20 text-green-400"
              : "bg-red-500/10 border border-red-500/20 text-red-400"
          }`}
          data-testid="liquidsoap-message"
        >
          {message.type === "success" ? (
            <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
          ) : (
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          )}
          <span className="text-sm">{message.text}</span>
        </div>
      )}

      {/* Save Button */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white px-6 py-3 rounded-xl transition font-medium"
          data-testid="liquidsoap-save-btn"
        >
          {saving ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {saving ? "Saving & Restarting..." : "Save & Apply"}
        </button>
        <p className="text-white/40 text-sm">
          This will save your config and restart the station backend
        </p>
      </div>
    </div>
  );
};
